// Puerta de entrada: tapa la app hasta que hay sesión de Entra (o vista previa
// declarada). Estados: comprobando → biometría → dentro · o → pantalla de login.
//
// 🔴 En Expo Go el login corporativo NO puede funcionar (ver lib/auth.ts): ahí
// la pantalla lo explica y ofrece «vista previa» — la app entera funciona con
// datos reales, solo el rol se elige a mano. En la build nativa, el botón de
// Microsoft es el camino normal y la vista previa no aparece.
import React, { useCallback, useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { OlasOna, Latido, Cargando } from '@/components/MarcaOna';
import { C, F, R } from '@/constants/theme';
import { alPerderSesion, api, ES_PRODUCCION, SERVIDOR } from '@/api/client';
import { cerrarSesion, completarLoginWeb, esExpoGo, loginDisponible, loginEntra, puertaBiometrica, sesionGuardada, type Sesion } from '@/lib/auth';

type Estado = 'comprobando' | 'fuera' | 'entrando' | 'dentro';

export default function LoginGate({ children }: {
  children: (sesion: Sesion | null, salir: () => void, capacidades: string[] | null, rolServidor: string | null, rolesServidor: string[]) => React.ReactNode;
}) {
  const insets = useSafeAreaInsets();
  const [estado, setEstado] = useState<Estado>('comprobando');
  const [sesion, setSesion] = useState<Sesion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previa, setPrevia] = useState(false);
  const [caps, setCaps] = useState<string[] | null>(null);
  const [rolSrv, setRolSrv] = useState<string | null>(null);
  /* TODOS sus roles: la app deja pasar de uno a otro sin que sea «simular». */
  const [rolesSrv, setRolesSrv] = useState<string[]>([]);

  /* 🔴 UN SOLO SITIO que interpreta lo que dice el servidor sobre ti, porque
     antes estaba copiado en tres y las tres colapsaban 401 y 403 en lo mismo:
     «capacidades vacías». El resultado era quedarse dentro mirando «SIN ACCESO»
     sin forma de salir del bucle. Ahora:
       · 401 → tu sesión ya no vale: se limpia y se vuelve a la puerta
       · 403 → estás identificado pero Hotelos no te da acceso: se dice
       · nada → sin red: se deja pasar con lo que haya (modo degradado) */
  const aplicarYo = useCallback(async (y: Awaited<ReturnType<typeof api.yo>>) => {
    if (y?.status === 401) {
      await cerrarSesion();
      setSesion(null); setCaps(null); setRolSrv(null); setRolesSrv([]);
      setError('Tu sesión ha caducado. Vuelve a entrar con tu cuenta de Ona.');
      setEstado('fuera');
      return;
    }
    if (y?.status === 403) {
      setCaps([]); setRolSrv(null); setRolesSrv([]);
      setError(y.motivo || 'Tu cuenta no tiene acceso a Ona Pulse. Pídeselo a Sistemas.');
      setEstado('fuera');
      return;
    }
    setCaps(y ? (y.identificado ? (y.capacidades ?? []) : []) : null);
    setRolSrv(y?.rol ?? null);
    setRolesSrv(y?.roles ?? []);
  }, []);

  /* Y si una petición cualquiera pierde la sesión mientras usas la app, se
     vuelve a la puerta en vez de dejar las pantallas mudas. */
  useEffect(() => {
    alPerderSesion(() => {
      cerrarSesion().finally(() => {
        setSesion(null); setCaps(null);
        setError('Tu sesión ha caducado. Vuelve a entrar con tu cuenta de Ona.');
        setEstado('fuera');
      });
    });
    return () => alPerderSesion(null);
  }, []);

  /* Al abrir: ¿hay sesión viva (o renovable)? → Face ID → dentro. */
  useEffect(() => {
    let vivo = true;
    (async () => {
      // ¿Volvemos de Microsoft? En web el login navega fuera y regresa con el
      // código en la dirección; hay que canjearlo antes de mirar la sesión.
      try {
        const nueva = await completarLoginWeb();
        if (nueva && vivo) {
          setSesion(nueva);
          api.yo().then(aplicarYo);
          setEstado('dentro');
          return;
        }
      } catch (e) {
        if (vivo) { setError(e instanceof Error ? e.message : 'No se pudo completar el inicio de sesión.'); setEstado('fuera'); }
        return;
      }
      const s = await sesionGuardada();
      if (!vivo) return;
      if (!s) { setEstado('fuera'); return; }
      const ok = await puertaBiometrica();
      if (!vivo) return;
      if (!ok) { setEstado('fuera'); return; }
      setSesion(s);
      api.yo().then(aplicarYo);
      setEstado('dentro');
    })();
    return () => { vivo = false; };
  }, []);

  const entrar = useCallback(async () => {
    setError(null);
    setEstado('entrando');
    try {
      const s = await loginEntra();
      /* La sesión se guarda YA. Antes se guardaba después de preguntar por el
         acceso, y un fallo pasajero de red tiraba un inicio de sesión que había
         ido bien —encima con el mensaje de «no tienes acceso», que manda a
         Sistemas a buscar un permiso que sí estaba—. */
      setSesion(s);
      // Entrar en Entra no basta: el acceso lo concede Hotelos. Se comprueba
      // AQUÍ para dar un mensaje claro, en vez de dejar pasar a alguien que se
      // encontraría todas las pantallas vacías sin saber por qué.
      const yo = await api.yo();
      if (yo && !yo.identificado && !yo.legado) {
        /* 🔴 EL MENSAJE DICE CONTRA QUÉ SERVIDOR SE PREGUNTÓ, y no es un
           adorno: el 24-08 una actualización por aire dejó el APK de producción
           hablando con el de desarrollo, donde esa cuenta no tiene permiso. El
           móvil decía «no tienes acceso» —cierto, pero en el servidor
           equivocado— y se buscó el fallo donde no estaba. Con el servidor a la
           vista se habría visto en un segundo. */
        const donde = ES_PRODUCCION ? '' : `\n\nOjo: la app está preguntando a ${SERVIDOR}, que NO es producción.`;
        const porque = yo.status === 401
          ? `El servidor no aceptó tu identificación${yo.motivo ? ` (${yo.motivo})` : ''}.`
          : 'Tu cuenta no tiene acceso a Ona Pulse todavía. Pídeselo a Sistemas: es un permiso de Hotelos.';
        await cerrarSesion();
        setSesion(null);
        setError(porque + donde);
        setEstado('fuera');
        return;
      }
      setCaps(yo ? (yo.identificado ? (yo.capacidades ?? []) : []) : null);
      setRolesSrv(yo?.roles ?? []);
      setRolSrv(yo?.rol ?? null);
      setSesion(s);
      setEstado('dentro');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo iniciar sesión.');
      setEstado('fuera');
    }
  }, []);

  const salir = useCallback(() => {
    cerrarSesion();
    setSesion(null);
    setPrevia(false);
    setEstado('fuera');
  }, []);

  if (estado === 'dentro' || previa) return <>{children(sesion, salir, caps, rolSrv, rolesSrv)}</>;

  return (
    <View style={[s.root, { paddingTop: insets.top + 40, paddingBottom: insets.bottom + 28 }]}>
      {/* 🔴 AQUÍ ESTABA LA TORTUGONA A 78 px, Y ERA EL PROBLEMA. Patricio, 25-08:
          «¿la ves bien o quizás está un poco infantil? Pensá que es el primer
          impacto que tendrán los usuarios». Tenía razón, y el motivo concreto es
          que la Tortugona es la mascota de EVENTOS INFANTILES: puesta como
          primera cosa que ve un director, promete algo distinto de lo que hay
          dentro (ADR, GOP, brecha de presupuesto). Y le robaba el sitio a la
          marca, que es lo único que esta pantalla tiene que hacer.
          La mascota sigue donde se la ha ganado: es Noa en el chat y es el trofeo
          en los Premios Noa. Ahí significa algo; aquí era decoración. */}
      <View style={s.marca}>
        <Text style={s.logo}>ona <Text style={{ color: C.aqua }}>pulse</Text></Text>
        <OlasOna width={46} />
      </View>

      <View style={s.centro}>
        {/* 🔴 MÁS GRANDE, pero por el ALTO. El SVG ya ocupa el 100 % del ancho, así
            que `width` solo controla su altura: 300 lo deja en 65 px en vez de 52
            (un 25 % más alto). «Agrandarlo un poco» —Patricio, 25-08. */}
        <Latido width={300} />
        <View style={{ flexDirection: 'row', gap: 26 }}>
          <View>
            <Text style={s.cifra}>49</Text>
            <Text style={s.cifraLbl}>hoteles operando</Text>
          </View>
          <View>
            <Text style={[s.cifra, { color: C.ink }]}>4</Text>
            <Text style={s.cifraLbl}>PMS en uno</Text>
          </View>
        </View>
        <View>
          <Text style={s.h}>El pulso del grupo,{'\n'}al minuto</Text>
          <Text style={s.sub}>
            Entra con tu cuenta corporativa. Tu portada se arma según tu
            responsabilidad.
          </Text>
        </View>
      </View>

      <View style={{ gap: 12 }}>
        {estado === 'comprobando' ? (
          /* El mismo latido que el resto de la app, en vez de la rueda del
             sistema: es la única pantalla donde el pulso ya es el protagonista. */
          <Cargando texto="Comprobando tu sesión…" alto={70} />
        ) : null}

        {error ? <Text style={s.error}>{error}</Text> : null}

        {!loginDisponible ? (
          <>
            <View style={s.avisoBox}>
              <Text style={s.avisoT}>{esExpoGo ? 'Estás en Expo Go' : 'Estás en el navegador'}</Text>
              <Text style={s.avisoM}>
                El inicio de sesión corporativo necesita la app instalada de verdad
                {esExpoGo ? ' (Expo Go no permite personalizar su identificador, y Microsoft lo rechaza)' : ''}.
                Llegará con TestFlight.
              </Text>
            </View>
            <Pressable style={s.btnPrimario} onPress={() => setPrevia(true)}>
              <Text style={s.btnPrimarioTxt}>Continuar en vista previa</Text>
            </Pressable>
            <Text style={s.pieMini}>
              Datos reales de Hotelos · el rol se elige a mano desde el avatar
            </Text>
          </>
        ) : (
          <>
            <Pressable
              style={[s.btnPrimario, estado === 'entrando' && { opacity: 0.6 }]}
              disabled={estado === 'entrando'}
              onPress={entrar}
            >
              <Text style={s.btnPrimarioTxt}>
                {estado === 'entrando' ? 'Abriendo Microsoft…' : 'Entrar con mi cuenta de Ona'}
              </Text>
            </Pressable>
            <Text style={s.pieMini}>Microsoft Entra ID · Face ID al reabrir</Text>
          </>
        )}
      </View>
    </View>
  );
}

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: C.bg, paddingHorizontal: 26 },
  /* La marca a la izquierda y las olas pegadas al borde derecho, a la misma
     altura: lo que pidió Patricio, y además deja respirar el nombre. */
  marca: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  logo: { fontFamily: F.display, fontSize: 26, color: C.ink },
  /* 🔴 EL BLOQUE, UN POCO MÁS ARRIBA. Estaba centrado en el hueco entero y
     quedaba mucho aire por encima (se ve en la captura de Patricio del 25-08:
     unos 400 px vacíos entre la marca y el pulso). Con 70 de relleno abajo, el
     centrado se calcula sobre menos alto y el bloque sube ~35 px — que es «un
     poco», no una mudanza. */
  centro: { flex: 1, justifyContent: 'center', gap: 18, paddingBottom: 70 },
  h: { fontFamily: F.display, fontSize: 30, lineHeight: 37, color: C.ink },
  cifra: { fontFamily: F.displayMed, fontSize: 34, color: C.aqua, fontVariant: ['tabular-nums'] },
  cifraLbl: { fontFamily: F.ui, fontSize: 11.5, color: C.muted, marginTop: 2 },
  sub: { fontFamily: F.ui, fontSize: 14.5, lineHeight: 22, color: C.muted, marginTop: 10 },
  error: {
    fontFamily: F.ui, fontSize: 12.5, color: C.bad, lineHeight: 18,
    backgroundColor: 'rgba(240,90,90,0.10)', borderRadius: 12, padding: 12,
  },
  avisoBox: {
    backgroundColor: 'rgba(245,180,80,0.10)', borderRadius: 14, padding: 14, gap: 4,
    borderWidth: 1, borderColor: 'rgba(245,180,80,0.22)',
  },
  avisoT: { fontFamily: F.uiSemi, fontSize: 12.5, color: C.warn },
  avisoM: { fontFamily: F.ui, fontSize: 12, lineHeight: 18, color: C.muted },
  btnPrimario: {
    backgroundColor: C.aqua, borderRadius: R.pill, paddingVertical: 16, alignItems: 'center',
    shadowColor: C.aqua, shadowOpacity: 0.3, shadowRadius: 20, shadowOffset: { width: 0, height: 6 }, elevation: 8,
  },
  btnPrimarioTxt: { fontFamily: F.uiSemi, fontSize: 15, color: '#06282E' },
  pieMini: { fontFamily: F.ui, fontSize: 11, color: C.faint, textAlign: 'center' },
});
